// Typed client for the /api/* Pages Functions. The browser never sees the
// Modal bearer token — that's held by the Functions, who forward it.
// `credentials: "include"` ensures the session cookie travels on every call.

import type {
  CreatorSession,
  CreatorTurn,
  JobResult,
  ManifestSummary,
} from "./types";

async function jsonOrThrow<T>(r: Response): Promise<T> {
  if (!r.ok) {
    const text = await r.text().catch(() => "");
    throw new Error(`${r.status} ${r.statusText}: ${text.slice(0, 200)}`);
  }
  return r.json() as Promise<T>;
}

const baseInit: RequestInit = { credentials: "include" };

export async function listManifests(): Promise<ManifestSummary[]> {
  const r = await fetch("/api/list", baseInit);
  const body = await jsonOrThrow<{ manifests: ManifestSummary[] }>(r);
  return body.manifests;
}

export async function runRecipe(payload: {
  recipe_ref?: string;
  md_text?: string;
  inputs?: Record<string, unknown>;
}): Promise<{ job_id: string }> {
  const r = await fetch("/api/run-recipe", {
    ...baseInit,
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(payload),
  });
  return jsonOrThrow<{ job_id: string }>(r);
}

export async function createRecipe(payload: {
  prompt: string;
  target_id: string;
  target_version: string;
  model?: string;
  max_attempts?: number;
}): Promise<{ job_id: string }> {
  const r = await fetch("/api/create-recipe", {
    ...baseInit,
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(payload),
  });
  return jsonOrThrow<{ job_id: string }>(r);
}

export async function getJob(jobId: string): Promise<JobResult> {
  const r = await fetch(`/api/jobs/${encodeURIComponent(jobId)}`, baseInit);
  return jsonOrThrow<JobResult>(r);
}

// ─── Recipe creator (interview loop) ─────────────────────────────────────────

export async function creatorStart(intent: string): Promise<{ session_id: string }> {
  const r = await fetch("/api/creator/start", {
    ...baseInit,
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ intent }),
  });
  return jsonOrThrow<{ session_id: string }>(r);
}

export async function creatorTurn(
  session_id: string,
  user_message: string,
): Promise<CreatorTurn> {
  const r = await fetch("/api/creator/turn", {
    ...baseInit,
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ session_id, user_message }),
  });
  return jsonOrThrow<CreatorTurn>(r);
}

export async function creatorApprove(
  session_id: string,
  target_id?: string,
  target_version?: string,
): Promise<{ manifest_ref: string; digest: string; manifest: unknown }> {
  const r = await fetch("/api/creator/approve", {
    ...baseInit,
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ session_id, target_id, target_version }),
  });
  return jsonOrThrow<{ manifest_ref: string; digest: string; manifest: unknown }>(r);
}

export async function creatorSession(session_id: string): Promise<CreatorSession> {
  const r = await fetch(
    `/api/creator/session?session_id=${encodeURIComponent(session_id)}`,
    baseInit,
  );
  return jsonOrThrow<CreatorSession>(r);
}
